import React, { useEffect, useState, useMemo, useRef } from "react";
import { Row, Col, Badge, Card } from "react-bootstrap";
import { FiCalendar, FiSun, FiGift } from "react-icons/fi";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { getHoliday } from "../../store/slices/holiday";

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const pad2 = (n) => String(n).padStart(2, "0");

const formatDateKey = (year, month, day) =>
  `${year}-${pad2(month + 1)}-${pad2(day)}`;

const toDateKey = (dateValue) => {
  const d = new Date(dateValue);
  return formatDateKey(d.getFullYear(), d.getMonth(), d.getDate());
};

const monthKey = (dateObj) => `${dateObj.getFullYear()}-${pad2(dateObj.getMonth() + 1)}`;

export default function StudentHoliday() {
  const dispatch = useDispatch();

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const today = new Date();
  const [viewMonth, setViewMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  );
  const [selectedDateKey, setSelectedDateKey] = useState(toDateKey(today));

  const calendarRef = useRef(null);
  const todayCellRef = useRef(null);

  const fetchHoliday = () => {
    setIsLoading(true);

    dispatch(getHoliday())
      .unwrap()
      .then((res) => {
        const holidays = res?.data || [];
        setData(holidays);
      })
      .catch(() => {
        toast.error("Failed to fetch Holiday details");
        setData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchHoliday();
  }, []);

  const holidayByDate = useMemo(() => {
    const map = {};

    data.forEach((item) => {
      if (!item.fromDate || !item.toDate) return;

      const start = new Date(item.fromDate);
      const end = new Date(item.toDate);
      const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const last = new Date(end.getFullYear(), end.getMonth(), end.getDate());

      while (cursor <= last) {
        const dateKey = formatDateKey(
          cursor.getFullYear(),
          cursor.getMonth(),
          cursor.getDate()
        );

        if (!map[dateKey]) {
          map[dateKey] = [];
        }
        map[dateKey].push(item);

        cursor.setDate(cursor.getDate() + 1);
      }
    });

    return map;
  }, [data]);

  const monthsWithHolidays = useMemo(() => {
    const set = new Set();

    Object.keys(holidayByDate).forEach((dateKey) => {
      const d = new Date(dateKey);
      if (d.getDay() !== 0) {
        set.add(monthKey(d));
      }
    });

    return set;
  }, [holidayByDate]);

  const monthDays = useMemo(() => {
    const year = viewMonth.getFullYear();
    const month = viewMonth.getMonth();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const leadingBlanks = new Date(year, month, 1).getDay();

    const days = [];

    for (let i = 0; i < leadingBlanks; i++) {
      days.push(null);
    }

    for (let d = 1; d <= totalDays; d++) {
      const dateKey = formatDateKey(year, month, d);

      const dateObj = new Date(year, month, d);
      const isSunday = dateObj.getDay() === 0;

      const holidays = holidayByDate[dateKey] || [];
      const status = holidays.length > 0 || isSunday ? "holiday" : "none";

      days.push({ day: d, dateKey, status, holidays });
    }

    return days;
  }, [viewMonth, holidayByDate]);

  const monthStats = useMemo(() => {
    let holidayDays = 0;
    let sundays = 0;
    const entries = new Set();

    monthDays.forEach((cell) => {
      if (!cell) return;

      const isSunday = new Date(cell.dateKey).getDay() === 0;
      if (isSunday) sundays += 1;

      if (cell.holidays.length > 0) {
        holidayDays += 1;
        cell.holidays.forEach((h) => entries.add(h._id));
      }
    });

    return {
      holidayDays,
      holidayEntries: entries.size,
      sundays,
      totalHolidays: data.length,
    };
  }, [monthDays, data]);


  const selectedCell = useMemo(
    () => monthDays.find((cell) => cell && cell.dateKey === selectedDateKey),
    [monthDays, selectedDateKey]
  );

  const monthLabel = viewMonth.toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  const prevMonthDate = new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1);
  const nextMonthDate = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1);

  const hasPrevMonthHoliday = monthsWithHolidays.has(monthKey(prevMonthDate));
  const hasNextMonthHoliday = monthsWithHolidays.has(monthKey(nextMonthDate));

  const goToPrevMonth = () => {
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    );
  };

  const goToNextMonth = () => {
    setViewMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    );
  };

  const handleGoToToday = () => {
    const now = new Date();
    setViewMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    setSelectedDateKey(toDateKey(now));

    setTimeout(() => {
      calendarRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      todayCellRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  const handleDayClick = (cell) => {
    if (!cell) return;
    setSelectedDateKey(cell.dateKey);
  };

  const isToday = (dateKey) => dateKey === toDateKey(today);

  const summaryCardStyle = {
    background: "linear-gradient(135deg, #FFFFFF 0%, #F8F4FF 55%, #F2ECFF 100%)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",

    border: "2px solid rgba(132,81,255,0.20)",

    borderRadius: "22px",

    boxShadow: `
    0 12px 30px rgba(132,81,255,0.15),
    inset 0 1px 1px rgba(255,255,255,0.9)
  `,

    transition: "all .3s ease",
    overflow: "hidden",
  };

  const summaryBodyStyle = {
    padding: "15px",
  };

  const presentIconStyle = {
    width: "50px",
    height: "50px",
    borderRadius: "20px",

    background:
      "linear-gradient(135deg, rgba(132,81,255,0.18), rgba(168,139,250,0.32))",

    backdropFilter: "blur(14px)",
    WebkitBackdropFilter: "blur(14px)",

    border: "1px solid rgba(132,81,255,0.22)",

    color: "#7C3AED",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    fontSize: "20px",

    boxShadow: `
      0 10px 24px rgba(132,81,255,.18),
      inset 0 1px 0 rgba(255,255,255,.7)
  `,
  };

  const countStyle = {
    fontSize: "34px",
    fontWeight: "800",
    lineHeight: "1",
    letterSpacing: "-1px",

    background: "linear-gradient(135deg, #8451FF 0%, #A855F7 55%, #C084FC 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",

    textShadow: "0 4px 12px rgba(132,81,255,0.15)",
    marginBottom: "6px",

    transition: "all .3s ease",
  };

  const labelStyle = {
    fontSize: "12px",
    fontWeight: "600",
    color: "#6B7280",
    letterSpacing: "0.4px",
    display: "flex",
    alignItems: "center",
    gap: "6px",

    opacity: 0.9,
  };

  const navWrapStyle = {
    position: "relative",
    display: "inline-flex",
  };

  const navBadgeStyle = {
    position: "absolute",
    top: "-4px",
    right: "-4px",
    width: "9px",
    height: "9px",
    borderRadius: "50%",
    background: "#8451FF",
    border: "2px solid #FFFFFF",
    boxShadow: "0 0 0 1px rgba(132,81,255,0.35)",
  };

  return (
    <>
      <div className="page-header">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <Link to="/student/home"><FiHome style={{ color: "white", marginTop: "-4px" }} /></Link>
            </div>


            <FiChevronRight className="separator" />

            <span className="active">Attendance</span>
          </div>

        </div>
      </div>
      {selectedCell && (
        <Card className="att-detail-card mb-4" style={{ border: "none" }}>
          <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <div className="att-detail-date">
                {new Date(selectedCell.dateKey).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "long",
                  year: "numeric",
                })}
              </div>

              {selectedCell.holidays.length > 0 ? (
                <div className="d-flex flex-wrap gap-2">
                  {selectedCell.holidays.map((h) => (
                    <span key={h._id}>{h.name}</span>
                  ))}
                </div>
              ) : new Date(selectedCell.dateKey).getDay() === 0 ? (
                <span>Sunday Holiday</span>
              ) : (
                <span className="text-muted">No holiday on this date</span>
              )}
            </div>

            {selectedCell.holidays.length > 0 ||
              new Date(selectedCell.dateKey).getDay() === 0 ? (
              <Badge bg="warning" text="dark">Holiday</Badge>
            ) : (
              <Badge bg="light" text="dark">No Record</Badge>
            )}
          </Card.Body>
        </Card>
      )}

      <Row className="g-3 mb-4 justify-content-center">
        <Col xs={6} md={3}>
          <Card style={summaryCardStyle}>
            <Card.Body className="d-flex align-items-center gap-3" style={summaryBodyStyle}>
              <div style={presentIconStyle}>
                <FiGift />
              </div>
              <div>
                <div style={countStyle}>{monthStats.holidayEntries}</div>
                <div style={labelStyle}>Holidays this month</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={6} md={3}>
          <Card style={summaryCardStyle}>
            <Card.Body className="d-flex align-items-center gap-3" style={summaryBodyStyle}>
              <div style={presentIconStyle}>
                <FiSun />
              </div>
              <div>
                <div style={countStyle}>{monthStats.sundays}</div>
                <div style={labelStyle}>Sundays this month</div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* <Col xs={6} md={3}>
            <Card style={summaryCardStyle}>
              <Card.Body className="d-flex align-items-center gap-3" style={summaryBodyStyle}>
                <div style={presentIconStyle}>
                  <FiCalendar />
                </div>
                <div>
                  <div style={countStyle}>{yearStats.withoutSunday}</div>
                  <div style={labelStyle}>This year (No Sunday)</div>
                </div>
              </Card.Body>
            </Card>
          </Col> */}

        {/* <Col xs={6} md={3}>
            <Card style={summaryCardStyle}>
              <Card.Body className="d-flex align-items-center gap-3" style={summaryBodyStyle}>
                <div style={presentIconStyle}>
                  <FiCalendar />
                </div>
                <div>
                  <div style={countStyle}>{yearStats.withSunday}</div>
                  <div style={labelStyle}>This year (With Sunday)</div>
                </div>
              </Card.Body>
            </Card>
          </Col> */}
      </Row>

      <Card className="att-cal-card" style={{ border: "none" }} ref={calendarRef}>
        <div className="att-legend mx-3">
          <div className="att-legend-item">
            <span className="att-legend-dot" style={{ background: "#ffc107" }} />
            Holiday
          </div>
          <div className="att-legend-item">
            <span className="att-legend-dot" style={{ background: "#c9cdd6" }} />
            No Record
          </div>
        </div>
        <Card.Body>
          <div className="att-cal-header">
            <div className="att-cal-nav">
              <div style={navWrapStyle}>
                <button
                  type="button"
                  className="att-cal-nav-btn"
                  onClick={goToPrevMonth}
                  aria-label="Previous month"
                >
                  <IoIosArrowBack />
                </button>
                {hasPrevMonthHoliday && <span style={navBadgeStyle} />}
              </div>

              <span className="att-cal-month-label">{monthLabel}</span>

              <div style={navWrapStyle}>
                <button
                  type="button"
                  className="att-cal-nav-btn"
                  onClick={goToNextMonth}
                  aria-label="Next month"
                >
                  <IoIosArrowForward />
                </button>
                {hasNextMonthHoliday && <span style={navBadgeStyle} />}
              </div>
            </div>

            <button type="button" className="att-today-btn" onClick={handleGoToToday}>
              <FiCalendar /> This Month
            </button>
          </div>

          {isLoading ? (
            <div className="text-center py-4 text-muted">Loading holiday details...</div>
          ) : (
            <>
              <div className="att-weekday-row">
                {WEEKDAY_LABELS.map((label) => (
                  <div key={label} className="att-weekday-label">
                    {label}
                  </div>
                ))}
              </div>

              <div className="att-day-grid">
                {monthDays.map((cell, idx) => {
                  if (!cell) {
                    return <div key={`blank-${idx}`} className="att-day-cell att-day-empty" />;
                  }

                  const classes = ["att-day-cell", `att-day-${cell.status}`];
                  if (isToday(cell.dateKey)) classes.push("att-day-today");
                  if (cell.dateKey === selectedDateKey) classes.push("att-day-selected");

                  return (
                    <div
                      key={cell.dateKey}
                      ref={isToday(cell.dateKey) ? todayCellRef : null}
                      className={classes.join(" ")}
                      onClick={() => handleDayClick(cell)}
                    >
                      {cell.day}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </Card.Body>
      </Card>

    </>
  );
}